(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-mdv-store-root]';
  const CONTAINER_SELECTOR = '.product-single__store-availability-container';
  const NATIVE_SELECT_SELECTOR = '.product-page--cart-form form .variants select#variant-listbox';
  const SIZE_SWATCH_SELECTOR = '.product-page--cart-form form .size_swatch';
  const GLOBAL_KEY = '__mdvStoreAvailabilityController';

  function parseJSON(value) {
    try { return JSON.parse(value || ''); } catch (error) { return null; }
  }

  function asString(value) {
    return value === undefined || value === null ? '' : String(value);
  }

  function unique(values) {
    return values.filter((value, index) => value && values.indexOf(value) === index);
  }

  function validateData(product, config) {
    if (!product || !config || !product.id || !product.handle || !Array.isArray(product.variants) || !product.variants.length) return false;
    if (!['stock', 'directory'].includes(config.mode) || !config.labels || !Array.isArray(config.stores) || !config.stores.length) return false;
    if (config.stores.some(store => !store || !store.id || !store.timezone || !Array.isArray(store.hours) || store.hours.length !== 7)) return false;
    if (config.mode === 'directory') return true;
    return Boolean(config.endpoint && /^https:\/\//.test(config.endpoint) && config.shop && config.locationIds && config.classifications);
  }

  function variantMap(product) {
    const map = Object.create(null);
    product.variants.forEach(variant => { if (variant && variant.id) map[asString(variant.id)] = variant; });
    return map;
  }

  function sizeOf(product, variant) {
    if (!variant || product.sizeIndex < 0) return '';
    return asString(variant.size || (variant.options || [])[product.sizeIndex]);
  }

  function colourOf(product, variant) {
    if (!variant || product.colourIndex < 0) return '';
    return asString(variant.colour || (variant.options || [])[product.colourIndex]);
  }

  function requiresExplicitSize(product) {
    if (product.sizeIndex < 0) return false;
    const sizes = unique(product.variants.map(variant => sizeOf(product, variant)));
    return sizes.length > 1;
  }

  function selectedSwatchValue(sizeSwatches) {
    if (!sizeSwatches) return '';
    const checked = sizeSwatches.querySelector('input[type="radio"]:checked');
    const swatch = checked ? checked.closest('.swatch-element') : sizeSwatches.querySelector('.swatch-element.selected');
    return swatch ? asString(swatch.getAttribute('data-value') || (checked && checked.value)) : '';
  }

  function resolveSelection(product, nativeSelect, sizeSwatches, explicitlyChosen = true) {
    const variants = variantMap(product);
    const variant = nativeSelect ? variants[asString(nativeSelect.value)] || null : null;
    const explicit = selectedSwatchValue(sizeSwatches);
    const needsSize = requiresExplicitSize(product);
    if (!variant) return { variant: null, size: explicit, confirmed: false, needsSize };
    const actualSize = sizeOf(product, variant);
    if (needsSize && !explicitlyChosen) return { variant, size: '', confirmed: false, needsSize };
    if (needsSize && (!explicit || explicit !== actualSize)) return { variant, size: explicit, confirmed: false, needsSize };
    return { variant, size: actualSize || explicit, confirmed: true, needsSize };
  }

  function locationsForClassification(config, classification) {
    if (!classification || !config.classifications) return [];
    const locations = config.classifications[asString(classification)];
    if (!Array.isArray(locations)) return [];
    const allowed = config.stores.map(store => asString(store.id));
    return unique(locations.map(asString)).filter(id => allowed.includes(id));
  }

  function stockFromResponse(product, payload) {
    if (!payload || payload.errors || Number(payload.status) !== 200 || !payload.data || typeof payload.data !== 'object') return null;
    const productStock = payload.data[asString(product.id)];
    return productStock && typeof productStock === 'object' && !Array.isArray(productStock) ? productStock : null;
  }

  function availabilityModel(product, config, stock, selection) {
    const labels = config.labels;
    if (config.mode === 'directory') {
      return { summary: labels.directory, size: selection.size, locations: [], selected: selection.confirmed };
    }
    if (!selection.confirmed || !selection.variant) {
      return { summary: labels.choose, size: '', locations: [], selected: false };
    }
    const classification = stock && stock[asString(selection.variant.id)];
    const locations = locationsForClassification(config, classification);
    let summary = labels.check;
    if (locations.length === 1) {
      const store = config.stores.find(item => asString(item.id) === locations[0]);
      summary = labels.at.replace('__STORE__', store ? store.name : '');
    } else if (locations.length > 1) {
      summary = labels.atMultiple.replace('__COUNT__', asString(locations.length));
    }
    return { summary, size: selection.size, locations, selected: true };
  }

  function stockURL(config, product) {
    const url = new URL(config.endpoint);
    url.searchParams.set('shop', config.shop);
    url.searchParams.set('location_id', config.locationIds);
    url.searchParams.set('product_id', asString(product.id));
    return url.toString();
  }

  function todayText(store, label, now) {
    let weekday;
    try {
      weekday = new Intl.DateTimeFormat('en-GB', { timeZone: store.timezone, weekday: 'short' }).format(now || new Date());
    } catch (error) { return ''; }
    const index = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[weekday];
    return index === undefined ? '' : label.replace('__HOURS__', asString(store.hours[index]));
  }

  function addListener(target, type, listener, options, cleanups) {
    target.addEventListener(type, listener, options);
    cleanups.push(() => target.removeEventListener(type, listener, options));
  }

  function createSizePicker(session) {
    const { root, dialog, picker, labels, cleanups } = session;
    const field = root.querySelector('.mdv-store-picker');
    const label = root.querySelector('#mdv-store-size-label');
    if (!field || !label || !picker.options) return null;
    const choices = Array.from(picker.options).filter(option => option.value);
    if (!choices.length) return null;
    const button = document.createElement('button');
    button.type = 'button';
    button.id = 'mdv-store-size-button';
    button.className = 'mdv-store-picker__button';
    button.setAttribute('role', 'combobox');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-labelledby', label.id + ' ' + button.id);
    const listbox = document.createElement('div');
    listbox.id = 'mdv-store-size-listbox';
    listbox.className = 'mdv-store-picker__listbox';
    listbox.setAttribute('role', 'listbox');
    listbox.setAttribute('aria-labelledby', label.id);
    listbox.hidden = true;
    button.setAttribute('aria-controls', listbox.id);
    const items = choices.map((option, index) => {
      const item = document.createElement('div');
      item.id = 'mdv-store-size-option-' + index;
      item.className = 'mdv-store-picker__option';
      item.setAttribute('role', 'option');
      item.dataset.index = asString(index);
      item.textContent = option.textContent;
      listbox.appendChild(item);
      return item;
    });
    let expanded = false;
    let active = -1;
    let lastValue = null;
    let search = '';
    let lastTyped = 0;

    function sync() {
      const selectedIndex = choices.findIndex(option => asString(option.value) === asString(picker.value));
      button.textContent = selectedIndex < 0 ? labels.choose : choices[selectedIndex].textContent;
      if (lastValue !== asString(picker.value)) {
        active = selectedIndex;
        lastValue = asString(picker.value);
      }
      button.setAttribute('aria-expanded', asString(expanded));
      listbox.hidden = !expanded;
      if (expanded && items[active]) button.setAttribute('aria-activedescendant', items[active].id);
      else button.removeAttribute('aria-activedescendant');
      items.forEach((item, index) => {
        item.setAttribute('aria-selected', asString(index === selectedIndex));
        item.dataset.active = asString(expanded && index === active);
      });
    }

    function reveal() {
      if (items[active]) items[active].scrollIntoView({ block: 'nearest' });
    }

    function close() {
      expanded = false;
      search = '';
      sync();
    }

    function open(preference) {
      if (!dialog.open || root.hidden) return;
      sync();
      active = preference === 'first' ? 0 : preference === 'last' ? items.length - 1 : Math.max(0, active);
      button.scrollIntoView({ block: 'nearest' });
      const scrollport = root.querySelector('.mdv-store-dialog__content');
      if (scrollport && scrollport.getBoundingClientRect && button.getBoundingClientRect) {
        const available = scrollport.getBoundingClientRect().bottom - button.getBoundingClientRect().bottom - 8;
        listbox.style.maxHeight = Math.max(44, Math.min(216, available)) + 'px';
      }
      expanded = true;
      sync();
      reveal();
    }

    function commit(index) {
      if (!expanded || !dialog.open || !choices[index]) return;
      picker.value = choices[index].value;
      picker.dispatchEvent(new Event('change', { bubbles: true }));
      close();
      button.focus({ preventScroll: true });
    }

    addListener(button, 'click', () => expanded ? close() : open(), false, cleanups);
    addListener(button, 'keydown', event => {
      const key = event.key;
      if (key === 'Escape' && expanded) {
        event.preventDefault();
        event.stopPropagation();
        close();
      } else if (key === 'Tab') {
        close();
      } else if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        if (expanded) commit(active); else open();
      } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) {
        event.preventDefault();
        if (!expanded) open(key === 'ArrowUp' || key === 'End' ? 'last' : 'first');
        else {
          active = key === 'Home' ? 0 : key === 'End' ? items.length - 1 : (active + (key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
          sync();
          reveal();
        }
      } else if (key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        if (!expanded) open();
        const now = Date.now();
        search = (now - lastTyped > 700 ? '' : search) + key.toLocaleLowerCase();
        lastTyped = now;
        const find = prefix => choices.findIndex(option => option.textContent.trim().toLocaleLowerCase().startsWith(prefix));
        let index = find(search);
        if (index < 0) { search = key.toLocaleLowerCase(); index = find(search); }
        if (index >= 0) { active = index; sync(); reveal(); }
      }
    }, false, cleanups);
    addListener(listbox, 'pointerdown', event => { if (event.target.closest('.mdv-store-picker__option')) event.preventDefault(); }, false, cleanups);
    addListener(listbox, 'click', event => {
      const item = event.target.closest('.mdv-store-picker__option');
      if (item && listbox.contains(item)) commit(Number(item.dataset.index));
    }, false, cleanups);
    field.appendChild(button);
    field.appendChild(listbox);
    picker.hidden = true;
    label.htmlFor = button.id;
    sync();
    cleanups.push(() => {
      picker.hidden = false;
      label.htmlFor = picker.id;
      if (button.parentNode) button.parentNode.removeChild(button);
      if (listbox.parentNode) listbox.parentNode.removeChild(listbox);
    });
    return { sync, close, field, get expanded() { return expanded; } };
  }

  function createController() {
    let session = null;
    let generation = 0;
    let pendingFrame = 0;

    function clearSession() {
      generation += 1;
      if (!session) return;
      const previous = session;
      session = null;
      if (previous.abort) previous.abort.abort();
      if (previous.hoursTimer) clearInterval(previous.hoursTimer);
      if (previous.observer) previous.observer.disconnect();
      if (previous.dialog && previous.dialog.open) previous.dialog.close();
      document.body.classList.remove('mdv-store-dialog-open');
      previous.container.classList.remove('mdv-store-enhanced');
      previous.root.hidden = true;
      previous.cleanups.splice(0).reverse().forEach(cleanup => { try { cleanup(); } catch (error) {} });
    }

    function fail(container, root) {
      if (container) container.classList.remove('mdv-store-enhanced');
      if (root) root.hidden = true;
      document.body.classList.remove('mdv-store-dialog-open');
      return false;
    }

    function nativeElements() {
      return {
        select: document.querySelector(NATIVE_SELECT_SELECTOR),
        sizes: document.querySelector(SIZE_SWATCH_SELECTOR)
      };
    }

    function render() {
      if (!session) return false;
      if (!session.root.isConnected) {
        const staleContainer = session.container;
        const staleRoot = session.root;
        clearSession();
        return fail(staleContainer, staleRoot);
      }
      const native = nativeElements();
      if (!native.select) {
        const staleContainer = session.container;
        const staleRoot = session.root;
        clearSession();
        return fail(staleContainer, staleRoot);
      }
      const selection = resolveSelection(session.product, native.select, native.sizes, session.hasExplicitSize);
      const actualVariant = selection.variant;
      session.picker.value = selection.confirmed && actualVariant ? asString(actualVariant.id) : '';
      if (session.sizePicker) session.sizePicker.sync();
      const model = availabilityModel(session.product, session.config, session.stock, selection);
      if (session.inventoryNotice && (!selection.confirmed || !actualVariant ||
          session.inventoryNotice.variantId !== asString(actualVariant.id))) session.inventoryNotice = null;
      const notice = session.inventoryNotice;
      const collection = Boolean(notice && notice.collection);
      const separateNotice = Boolean(notice && !collection);
      session.summary.textContent = collection ? notice.message : model.summary;
      session.trigger.dataset.available = asString(collection || model.locations.length > 0);
      session.trigger.hidden = separateNotice;
      if (session.inventoryStatus) {
        session.inventoryStatus.textContent = separateNotice ? notice.message : '';
        session.inventoryStatus.hidden = !separateNotice;
      }
      const other = session.trigger.querySelector('.mdv-store-trigger__link');
      if (other) other.textContent = session.labels.other || '';
      session.root.querySelectorAll('[data-mdv-store-status]').forEach(row => {
        const id = asString(row.getAttribute('data-mdv-store-status'));
        const available = model.locations.includes(id);
        const base = available ? session.labels.available : (model.selected ? session.labels.confirm : session.labels.choose);
        row.textContent = base + (model.size ? ' · ' + model.size : '');
        row.dataset.available = asString(available);
      });
      const colour = session.root.querySelector('[data-mdv-store-colour]');
      const price = session.root.querySelector('[data-mdv-store-price]');
      if (actualVariant && colour) colour.textContent = colourOf(session.product, actualVariant);
      if (actualVariant && price) price.textContent = asString(actualVariant.price);
      return true;
    }

    function setInventoryStatus(source, message, collection) {
      if (!session || !session.root.isConnected || session.root.hidden) return false;
      const native = nativeElements();
      const form = source && source.closest && source.closest('form');
      if (!native.select || !form || !form.contains(native.select)) return false;
      const variantId = asString(native.select.value);
      session.inventoryNotice = message ? {
        variantId, message,
        collection: collection === true
      } : null;
      if (!session.inventoryStatus) {
        const status = document.createElement('p');
        status.className = 'mdv-store-inventory-status';
        status.setAttribute('role', 'status');
        status.hidden = true;
        session.root.insertBefore(status, session.trigger);
        session.inventoryStatus = status;
        session.cleanups.push(() => { if (status.parentNode) status.parentNode.removeChild(status); });
      }
      return render();
    }

    function scheduleRender() {
      if (pendingFrame) return;
      pendingFrame = requestAnimationFrame(() => { pendingFrame = 0; render(); });
    }

    function updateToday() {
      if (!session) return;
      session.config.stores.forEach(store => {
        const row = Array.from(session.root.querySelectorAll('[data-mdv-store-today]')).find(item => asString(item.getAttribute('data-mdv-store-today')) === asString(store.id));
        if (!row) return;
        const text = todayText(store, session.labels.today, new Date());
        row.textContent = text;
        row.hidden = !text;
      });
    }

    function closeDialog() {
      if (!session || !session.dialog.open) return;
      session.dialog.close();
    }

    function focusable(dialog) {
      return Array.from(dialog.querySelectorAll('a[href],button:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])')).filter(node => !node.hidden);
    }

    function bindSession(current) {
      const { root, trigger, dialog, picker, cleanups } = current;
      addListener(trigger, 'click', () => {
        if (!render()) return;
        updateToday();
        current.returnFocus = trigger;
        dialog.showModal();
        document.body.classList.add('mdv-store-dialog-open');
        clearInterval(current.hoursTimer);
        current.hoursTimer = setInterval(updateToday, 60000);
        const first = root.querySelector('[data-mdv-store-close]') || focusable(dialog)[0];
        if (first) first.focus({ preventScroll: true });
      }, false, cleanups);
      root.querySelectorAll('[data-mdv-store-close]').forEach(button => addListener(button, 'click', closeDialog, false, cleanups));
      addListener(dialog, 'click', event => { if (event.target === dialog) closeDialog(); }, false, cleanups);
      addListener(dialog, 'cancel', event => {
        if (current.sizePicker && current.sizePicker.expanded) {
          event.preventDefault();
          current.sizePicker.close();
        }
      }, false, cleanups);
      addListener(dialog, 'keydown', event => {
        if (event.key !== 'Tab') return;
        if (current.sizePicker) current.sizePicker.close();
        const nodes = focusable(dialog);
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }, false, cleanups);
      addListener(dialog, 'close', () => {
        clearInterval(current.hoursTimer);
        current.hoursTimer = null;
        if (current.sizePicker) current.sizePicker.close();
        document.body.classList.remove('mdv-store-dialog-open');
        if (current.returnFocus && current.returnFocus.isConnected) current.returnFocus.focus({ preventScroll: true });
      }, false, cleanups);
      addListener(picker, 'change', () => {
        const desired = current.variants[asString(picker.value)];
        if (!desired) { scheduleRender(); return; }
        const size = sizeOf(current.product, desired);
        const native = nativeElements();
        if (current.product.sizeIndex < 0 || !native.sizes) { scheduleRender(); return; }
        const swatch = Array.from(native.sizes.querySelectorAll('.swatch-element[data-value]')).find(item => asString(item.getAttribute('data-value')) === size);
        const label = swatch && swatch.querySelector('label');
        if (label) {
          current.hasExplicitSize = true;
          label.click();
        }
        scheduleRender();
      }, false, cleanups);
      const native = nativeElements();
      if (native.sizes) {
        current.observer = new MutationObserver(scheduleRender);
        current.observer.observe(native.sizes, { attributes: true, subtree: true, attributeFilter: ['class', 'checked'] });
      }
    }

    function fetchStock(current, token) {
      if (current.config.mode !== 'stock') return;
      current.abort = new AbortController();
      const timeout = setTimeout(() => current.abort.abort(), 3000);
      current.cleanups.push(() => clearTimeout(timeout));
      fetch(stockURL(current.config, current.product), {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal: current.abort.signal
      }).then(response => {
        if (!response.ok) throw new Error('stock response ' + response.status);
        return response.json();
      }).then(payload => {
        if (!session || session !== current || token !== generation) return;
        current.stock = stockFromResponse(current.product, payload);
        render();
      }).catch(() => {
        if (!session || session !== current || token !== generation) return;
        current.stock = null;
        render();
      }).finally(() => clearTimeout(timeout));
    }

    function init() {
      clearSession();
      const container = document.querySelector(CONTAINER_SELECTOR);
      const root = container && container.querySelector(ROOT_SELECTOR);
      if (!container || !root) return fail(container, root);
      const product = parseJSON(root.getAttribute('data-product'));
      const config = parseJSON(root.getAttribute('data-config'));
      const trigger = root.querySelector('[data-mdv-store-open]');
      const dialog = root.querySelector('#mdv-store-dialog');
      const picker = root.querySelector('#mdv-store-size');
      const summary = root.querySelector('[data-mdv-store-summary]');
      const native = nativeElements();
      if (!validateData(product, config) || !trigger || !dialog || typeof dialog.showModal !== 'function' || typeof dialog.close !== 'function' || !picker || !summary || !native.select) return fail(container, root);
      const current = {
        container, root, product, config, trigger, dialog, picker, summary,
        labels: config.labels, variants: variantMap(product), stock: null,
        cleanups: [], abort: null, observer: null, hoursTimer: null,
        returnFocus: null, sizePicker: null,
        hasExplicitSize: !requiresExplicitSize(product)
      };
      session = current;
      root.hidden = false;
      current.sizePicker = createSizePicker(current);
      if (!current.sizePicker && requiresExplicitSize(product)) { clearSession(); return fail(container, root); }
      bindSession(current);
      container.classList.add('mdv-store-enhanced');
      render();
      const token = generation;
      fetchStock(current, token);
      return true;
    }

    function onDocumentInteraction(event) {
      if (!session || !(event.target instanceof Element)) return;
      if (event.target.closest(SIZE_SWATCH_SELECTOR)) {
        session.hasExplicitSize = true;
        scheduleRender();
      } else if (event.target.matches(NATIVE_SELECT_SELECTOR)) scheduleRender();
    }

    function onPointerDown(event) {
      if (!session || !session.sizePicker || !session.sizePicker.expanded) return;
      if (!session.sizePicker.field.contains(event.target)) session.sizePicker.close();
    }

    function onColourSwitched() {
      clearSession();
      requestAnimationFrame(init);
    }

    document.addEventListener('click', onDocumentInteraction);
    document.addEventListener('change', onDocumentInteraction);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('mdv:colour-switched', onColourSwitched);
    document.addEventListener('visibilitychange', () => { if (!document.hidden && session && session.dialog.open) updateToday(); });
    window.addEventListener('resize', () => { if (session && session.sizePicker) session.sizePicker.close(); });
    window.addEventListener('popstate', scheduleRender);

    return { init, dispose: clearSession, render, setInventoryStatus, get session() { return session; } };
  }

  const internals = {
    validateData, variantMap, sizeOf, colourOf, requiresExplicitSize, selectedSwatchValue,
    resolveSelection, locationsForClassification, stockFromResponse, availabilityModel,
    stockURL, todayText, createSizePicker, createController
  };
  if (window.__MDV_STORE_AVAILABILITY_TEST__) window.__MDV_STORE_AVAILABILITY_TEST__.internals = internals;
  if (window[GLOBAL_KEY]) return;
  if (window.__MDV_STORE_AVAILABILITY_TEST__ && window.__MDV_STORE_AVAILABILITY_TEST__.captureOnly) return;
  const controller = createController();
  window[GLOBAL_KEY] = controller;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', controller.init, { once: true });
  else controller.init();
})();
